"use client";

import { useState } from "react";

declare global {
  interface Window {
    Mews?: any;
  }
}

type BookNowProps = {
  className?: string;
  children: React.ReactNode;
  hotel?: string;
  from?: string;
  to?: string;
  adults?: number;
  variant?: 'default' | 'main';
};

const HOTEL_TO_CONFIG: Record<string, string> = {
  dottingen: "5ce23cfa-59c9-41c5-8da4-b0ff00945dda",
  // littau: "<ID>",
};

export function BookNow({ 
  className = "", 
  children, 
  hotel = "dottingen",
  from,
  to,
  adults = 1,
  variant = 'default'
}: BookNowProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = () => {
    setIsLoading(true);

    // Load Mews script if not already loaded
    const loadMewsScript = () => {
      if (window.Mews) {
        initializeMews();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://api.mews.com/distributor/distributor.min.js';
      script.onload = () => {
        console.log('Mews script loaded');
        initializeMews();
      };
      script.onerror = () => {
        console.error('Failed to load Mews script');
        setIsLoading(false);
      };
      document.head.appendChild(script);
    };

    const initializeMews = () => {
      try {
        window.Mews.Distributor(
          {
            configurationIds: [HOTEL_TO_CONFIG[hotel] || HOTEL_TO_CONFIG.dottingen]
          },
          function (api: any) {
            console.log("Mews API ready");

            // Set basic configuration
            api.setLanguageCode("en");
            api.setCurrencyCode("CHF");

            // Set booking parameters
            if (from) {
              const fromDate = new Date(from);
              api.setStartDate(fromDate);
            }
            if (to) {
              const toDate = new Date(to);
              api.setEndDate(toDate);
            }
            if (adults) {
              api.setAdultCount(adults);
            }

            // Open the booking widget
            console.log("Opening booking widget...");
            api.open();
            
            setIsLoading(false);
          },
          { dataBaseUrl: "https://api.mews.com" }
        );
      } catch (error) {
        console.error("Mews initialization error:", error);
        setIsLoading(false);
      }
    };

    loadMewsScript();
  };

  // Base classes for both variants
  const baseClasses = "distributor cursor-pointer relative z-10 py-2.5 px-4 flex items-center gap-2 font-medium w-fit transition rounded-4xl border disabled:opacity-50 disabled:cursor-not-allowed";
  
  // Variant-specific classes
  const variantClasses = variant === 'main' 
    ? "hover:bg-black hover:text-white border-black text-black"
    : "border"; // HeaderMain variant - just border, no specific colors

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`${baseClasses} ${variantClasses} ${className}`}
    >
      {isLoading ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
          Loading...
        </>
      ) : (
        children
      )}
    </button>
  );
}
